import type { ReactNode } from 'react';

/*
 * Stavy, ktore ma mat KAZDA obrazovka nacitavajuca data:
 * nacitavanie, chyba a prazdny zoznam. Poziadavka z CLAUDE.md.
 *
 * Su tu ako spolocne komponenty prave preto, aby sa nedali obist -
 * obrazovka, ktora ich nepouzije, je viditelne nekompletna.
 *
 * Ikony su vlastne SVG. Emoji sa nepouzivaju nikde, bez vynimky.
 */

function Spinner(): ReactNode {
  return (
    <svg
      className="size-6 animate-spin text-(--color-text-muted)"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path
        d="M22 12a10 10 0 0 0-10-10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function WarningIcon(): ReactNode {
  return (
    <svg className="size-6" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function EmptyIcon(): ReactNode {
  return (
    <svg className="size-6" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3 7.5 12 3l9 4.5v9L12 21l-9-4.5v-9Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="m3 7.5 9 4.5 9-4.5M12 12v9" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

interface PanelProps {
  readonly icon: ReactNode;
  readonly title: string;
  readonly detail?: string;
  readonly action?: ReactNode;
  readonly tone?: 'neutral' | 'danger';
  readonly role?: 'status' | 'alert';
}

function Panel({ icon, title, detail, action, tone = 'neutral', role = 'status' }: PanelProps) {
  const toneClass = tone === 'danger' ? 'text-(--color-danger)' : 'text-(--color-text-muted)';
  return (
    <div
      role={role}
      aria-live={role === 'alert' ? 'assertive' : 'polite'}
      className="flex flex-col items-center justify-center gap-3 rounded-(--radius-card) border border-(--color-border) bg-(--color-surface-raised) px-6 py-12 text-center"
    >
      <span className={toneClass}>{icon}</span>
      <p className="text-base font-medium text-(--color-text)">{title}</p>
      {detail !== undefined && (
        <p className="max-w-prose text-sm text-(--color-text-muted)">{detail}</p>
      )}
      {action}
    </div>
  );
}

export function LoadingState({ what = 'Načítavam' }: { readonly what?: string }): ReactNode {
  return <Panel icon={<Spinner />} title={what} />;
}

/**
 * Chybovy stav.
 *
 * `detail` je urceny pouzivatelovi a NESMIE obsahovat technicke podrobnosti -
 * tie patria do logu servera. Poziadavka z CLAUDE.md.
 */
export function ErrorState({
  title = 'Niečo sa nepodarilo',
  detail,
  onRetry,
}: {
  readonly title?: string;
  readonly detail?: string;
  readonly onRetry?: () => void;
}): ReactNode {
  return (
    <Panel
      role="alert"
      tone="danger"
      icon={<WarningIcon />}
      title={title}
      {...(detail !== undefined ? { detail } : {})}
      {...(onRetry
        ? {
            action: (
              <button
                type="button"
                onClick={onRetry}
                className="rounded-(--radius-control) bg-(--color-accent) px-4 py-2 text-sm font-medium text-(--color-accent-text)"
              >
                Skúsiť znova
              </button>
            ),
          }
        : {})}
    />
  );
}

export function EmptyState({
  title,
  detail,
  action,
}: {
  readonly title: string;
  readonly detail?: string;
  readonly action?: ReactNode;
}): ReactNode {
  return (
    <Panel
      icon={<EmptyIcon />}
      title={title}
      {...(detail !== undefined ? { detail } : {})}
      {...(action !== undefined ? { action } : {})}
    />
  );
}
